import discovery from './discovery.ts';
import getKeyFn from './get-key-fn.ts';
import { jwtVerify } from './jose/jwt/verify.ts';
import validate, { defaultValidators } from './validate.ts';
import type { JwtVerifierOptions, Validators, VerifyJwt, VerifyJwtResult } from '../../types/jose/jwt';

export class JwtVerifyError extends Error {}

const jwtVerifier = (
    {
        issuerBaseUri = '',
        jwksUri = '',
        issuer = '',
        audience = '',
        tokenSigningAlg,
        timeout = 5000, // default timeout
        cacheMaxAge = 600000, // default cache max age
        clockTolerance = 5, // default clock tolerance in seconds
        maxTokenAge,
        strict = false,
        validators: customValidators,
        fetch,
        URL
    }: JwtVerifierOptions): VerifyJwt => {

    let algorithms: string[] | undefined;
    let validators: Validators;

    if (!(issuerBaseUri || (issuer && jwksUri))) {
        throw new Error(`Either 'issuerBaseUri' or both 'issuer' and 'jwksUri' must be provided`);
    }
    if (!audience) {
        throw new Error(`An 'audience' is required to validate the 'aud' claim`);
    }

    const getDiscovery = discovery({
        issuerBaseUri,
        timeout,
        cacheMaxAge,
        fetch,
        URL
    });

    const getKeyFnGetter = getKeyFn({ timeout, cacheMaxAge, fetch, URL });

    return async (jwt: string): Promise<VerifyJwtResult> => {
        try {
            if (issuerBaseUri) {
                const {
                    jwks_uri: discoveredJwksUri,
                    issuer: discoveredIssuer,
                    id_token_signing_alg_values_supported: idTokenSigningAlgValuesSupported
                } = await getDiscovery();
                jwksUri = jwksUri || discoveredJwksUri;
                issuer = issuer || discoveredIssuer;
                algorithms = idTokenSigningAlgValuesSupported;
            }
            validators ??= {
                ...defaultValidators(
                    issuer,
                    audience,
                    clockTolerance,
                    maxTokenAge,
                    strict,
                    algorithms,
                    tokenSigningAlg
                ),
                ...customValidators
            };
            const {payload, protectedHeader: header} = await jwtVerify(jwt, getKeyFnGetter(jwksUri), {clockTolerance});
            validate(payload, header, validators);
            return {payload, header, token: jwt};
        } catch (e) {
            throw new JwtVerifyError(`${e instanceof Error ? e.message : String(e)}`);
        }
    }
}

export default jwtVerifier;
